/* PDP recommendations loader.

   The section renders an empty grid on a normal page load, because Shopify only
   populates the `recommendations` Liquid object when the section is requested
   through the Product Recommendations API. This fetches that endpoint for the
   current product and swaps the returned, populated grid in.

   Design contract (set on the section element):
     data-pdp-recommendations  marker
     data-product-id           numeric product id
     data-section-id           section id, e.g. template--123__recommendations
     data-limit                how many to request
     data-intent               "related" (default) or "complementary"

   product-cards.js binds cart/carousel behaviour by delegation on document, so
   the injected cards work without re-initialising anything here.
*/

(() => {
  if (window.CaratKinPdpReco) return;
  window.CaratKinPdpReco = true;

  const root = () => window.Shopify?.routes?.root || '/';

  /* ----------------------------------------------------------------
     Mobile slider + dot pagination

     product-detail.css turns the viewport into an overflow-x scroller
     and the grid into a flex row on mobile; the dots are hidden and the
     viewport doesn't scroll on desktop, so this is inert there. We light
     the dot for the card nearest the left edge and scroll a card into
     view on tap. Wired after the grid/dots are injected (below), since
     both are empty on first paint.
  ---------------------------------------------------------------- */
  function wireSlider(section) {
    const viewport = section.querySelector('[data-recommendations-viewport]');
    const grid = section.querySelector('[data-recommended-products]');
    const dotsWrap = section.querySelector('[data-recommendation-dots]');
    if (!viewport || !grid || !dotsWrap) return;

    const cards = [...grid.children];
    const dots = [...dotsWrap.querySelectorAll('button')];
    if (cards.length < 2 || !dots.length) return;

    // Distance between two adjacent cards, measured live (it is 0 while the
    // section is hidden, so always read it at event time).
    const step = () => {
      const a = cards[0].getBoundingClientRect();
      const b = cards[1].getBoundingClientRect();
      const s = b.left - a.left;
      return s > 0 ? s : (viewport.clientWidth || 1);
    };

    const setActive = (index) => {
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    // Dots follow the positions the strip can actually stop at. With several
    // cards in view that is fewer than the cards, so the spare dots are hidden
    // and the last dot lights up when the strip is scrolled to its end.
    const stops = () => {
      const max = viewport.scrollWidth - viewport.clientWidth;
      if (max <= 2) return 1;
      // Rounded up: a final, shorter step to the end is still a position.
      return Math.min(dots.length, Math.ceil(max / step() - 0.05) + 1);
    };

    const layoutDots = () => {
      const count = stops();
      dots.forEach((dot, i) => {
        dot.style.display = count > 1 && i < count ? '' : 'none';
      });
      return count;
    };

    const activeIndex = () => {
      const count = layoutDots();
      const max = viewport.scrollWidth - viewport.clientWidth;
      if (max > 2 && viewport.scrollLeft >= max - 2) return count - 1;
      return Math.min(count - 1, Math.max(0, Math.round(viewport.scrollLeft / step())));
    };

    const sync = () => {
      setActive(activeIndex());
    };

    window.addEventListener('resize', sync);

    let frame = 0;
    viewport.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    }, { passive: true });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        setActive(i); // reflect immediately; the scroll listener keeps it in sync on swipe
        viewport.scrollTo({ left: i * step(), behavior: 'smooth' });
      });
    });

    sync();
  }

  async function load(section) {
    const productId = section.dataset.productId;
    const sectionId = section.dataset.sectionId;
    const limit = section.dataset.limit || '4';
    const intent = section.dataset.intent || 'related';
    const grid = section.querySelector('[data-recommended-products]');
    if (!productId || !sectionId || !grid) return;

    // Already filled (e.g. server-rendered through the reco endpoint) -> just
    // wire the slider over the existing cards.
    if (grid.children.length > 0) { wireSlider(section); return; }

    try {
      const url =
        `${root()}recommendations/products` +
        `?section_id=${encodeURIComponent(sectionId)}` +
        `&product_id=${encodeURIComponent(productId)}` +
        `&limit=${encodeURIComponent(limit)}` +
        `&intent=${encodeURIComponent(intent)}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return;

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-recommended-products]');
      const freshDots = doc.querySelector('[data-recommendation-dots]');

      if (fresh) grid.innerHTML = fresh.innerHTML;
      const dots = section.querySelector('[data-recommendation-dots]');
      if (dots && freshDots) dots.innerHTML = freshDots.innerHTML;

      // No recommendations came back: hide the section so there is no stranded
      // heading over an empty row.
      if (grid.children.length === 0) {
        section.hidden = true;
      } else {
        // Cards and dots now exist — wire the mobile slider over them.
        wireSlider(section);
      }
    } catch {
      section.hidden = true;
    }
  }

  document.querySelectorAll('[data-pdp-recommendations]').forEach(load);
})();
